import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import InteractivePlot from './InteractivePlot';

const MissingImputation = () => {
  const [step, setStep] = useState(0);
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [selectedDatasetId, setSelectedDatasetId] = useState(null);
  const [columns, setColumns] = useState([]);
  const [savedParams, setSavedParams] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [chartTitle, setChartTitle] = useState(() => localStorage.getItem('mi_chart_title') || 'Missing Imputation Analysis');
  const [chartXLabel, setChartXLabel] = useState(() => localStorage.getItem('mi_chart_xlabel') || 'Category');
  const [chartYLabel, setChartYLabel] = useState(() => localStorage.getItem('mi_chart_ylabel') || 'Target Variable');
  const [showChartEditor, setShowChartEditor] = useState(false);

  const [formData, setFormData] = useState({
    Default_Date: null, risk_driver: null, Target_variable: null, Imputation_method: null,
    constant_value: '', mean_median_mode: 'Mean', n_neighbors: 2, weights: 'uniform', new_column_name: ''
  });

  const columnOptions = columns.map(c => ({ value: c, label: c }));
  const methodOptions = [
    { value: 'Remove rows', label: 'Remove rows' },
    { value: 'Constant', label: 'Constant' },
    { value: 'Mean/Median/Mode', label: 'Mean/Median/Mode' },
    { value: 'KNN', label: 'KNN' }
  ];

  useEffect(() => { fetchDatasets(); }, []);

  const fetchDatasets = async () => {
    try { const res = await axios.get('/api/missing-imputation/datasets'); setDatasets(res.data.datasets || []); }
    catch { toast.error('Failed to load datasets'); }
  };

  const handleSelectDataset = async () => {
    if (!selectedDataset) return;
    setLoading(true);
    try {
      const res = await axios.post(`/api/missing-imputation/select-dataset?dataset_id=${selectedDataset.value}`);
      setColumns(res.data.columns || []);
      setSelectedDatasetId(selectedDataset.value);
      toast.success(`Loaded: ${res.data.shape[0].toLocaleString()} rows`);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleSaveParameters = async () => {
    if (!formData.risk_driver || !formData.Target_variable || !formData.Imputation_method) {
      toast.error('Please fill all required fields'); return;
    }
    setLoading(true);
    try {
      await axios.post('/api/missing-imputation/step0', {
        Default_Date: formData.Default_Date?.value || '',
        risk_driver: formData.risk_driver.value,
        Target_variable: formData.Target_variable.value,
        Imputation_method: formData.Imputation_method.value
      });
      setSavedParams({
        Default_Date: formData.Default_Date?.value || '',
        risk_driver: formData.risk_driver.label,
        Target_variable: formData.Target_variable.label,
        Imputation_method: formData.Imputation_method.label
      });
      toast.success('Parameters saved'); setStep(1);
    } catch { toast.error('Failed to save'); }
    finally { setLoading(false); }
  };

  const handleRunImputation = async () => {
    setLoading(true);
    try {
      const payload = {};
      if (formData.Imputation_method?.value === 'Constant') payload.constant_value = formData.constant_value;
      else if (formData.Imputation_method?.value === 'Mean/Median/Mode') payload.mean_median_mode = formData.mean_median_mode;
      else if (formData.Imputation_method?.value === 'KNN') { payload.n_neighbors = formData.n_neighbors; payload.weights = formData.weights; }
      
      const res = await axios.post('/api/missing-imputation/step1', payload);
      setResults(res.data.results); toast.success(res.data.message); setStep(2);
    } catch { toast.error('Imputation failed'); }
    finally { setLoading(false); }
  };

  const handleSaveColumn = async () => {
    if (!formData.new_column_name) { toast.error('Enter a column name'); return; }
    setSaving(true);
    try { await axios.post('/api/missing-imputation/step2', { new_column_name: formData.new_column_name }); toast.success('Saved!'); setFormData({ ...formData, new_column_name: '' }); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleExport = async () => {
    try {
      const customTitles = {
        title: localStorage.getItem('mi_chart_title') || chartTitle,
        xlabel: localStorage.getItem('mi_chart_xlabel') || chartXLabel,
        ylabel: localStorage.getItem('mi_chart_ylabel') || chartYLabel
      };
      const res = await axios.post('/api/missing-imputation/export-with-titles', customTitles, { responseType: 'blob' })
        .catch(() => axios.get('/api/missing-imputation/export', { responseType: 'blob' }));
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'missing_imputation.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const cs = { control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px', borderColor: '#e0e0e0' }), valueContainer: (b) => ({ ...b, padding: '0 8px' }), placeholder: (b) => ({ ...b, fontSize: '13px', color: '#95a5a6' }), menu: (b) => ({ ...b, zIndex: 9999 }), menuPortal: (b) => ({ ...b, zIndex: 9999 }) };
  const stepTabs = ['Select', 'Review', 'Results'];
  const gs = { border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '16px', overflow: 'hidden' };
  const ghs = { background: '#f5f7fa', padding: '12px 16px', fontWeight: 600, fontSize: '14px', color: '#2c3e50' };
  const gcs = { padding: '16px', background: 'white' };
  const inputStyle = { width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px' };
  const tableHeaders = ['Category', 'Obs Before', 'Obs After', '% Before', '% After', 'Avg Target Before', 'Avg Target After'];

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '22px', marginBottom: '16px', color: '#2c3e50' }}>Missing Imputation</h1>

      <div style={{ display: 'flex', gap: '2px', marginBottom: '20px', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px' }}>
        {stepTabs.map((tab, idx) => (
          <button key={idx} onClick={() => { if (idx === 0) setStep(0); else if (idx === 1 && savedParams.Imputation_method) setStep(1); else if (idx === 2 && results) setStep(2); }}
            style={{ padding: '6px 12px', fontSize: '11px', background: step === idx ? '#1abc9c' : 'transparent', color: step === idx ? 'white' : '#7f8c8d', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{tab}</button>
        ))}
      </div>

      {step === 0 && (
        <div>
          <div style={gs}><div style={ghs}>📊 Select Dataset</div><div style={gcs}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <Select options={datasets.map(d => ({ value: d.id, label: `${d.name} (${d.rows?.toLocaleString()} rows)` }))} value={selectedDataset} onChange={setSelectedDataset} styles={cs} menuPortalTarget={document.body} placeholder="Select a dataset..." />
              </div>
              <button onClick={handleSelectDataset} disabled={!selectedDataset || loading}
                style={{ padding: '10px 24px', background: loading ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '6px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                {loading ? 'Loading...' : 'Load'}
              </button>
            </div>
          </div></div>

          {columns.length > 0 && (
            <div style={gs}><div style={ghs}>⚙️ Parameters</div><div style={gcs}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div><label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Default Date (optional)</label><Select options={columnOptions} value={formData.Default_Date} onChange={v => setFormData({...formData, Default_Date: v})} isClearable styles={cs} menuPortalTarget={document.body} placeholder="Select date..." /></div>
                <div><label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Imputation Method *</label><Select options={methodOptions} value={formData.Imputation_method} onChange={v => setFormData({...formData, Imputation_method: v})} styles={cs} menuPortalTarget={document.body} placeholder="Select method..." /></div>
                <div><label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Risk Driver *</label><Select options={columnOptions} value={formData.risk_driver} onChange={v => setFormData({...formData, risk_driver: v})} styles={cs} menuPortalTarget={document.body} placeholder="Select risk driver..." /></div>
                <div><label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Target Variable *</label><Select options={columnOptions} value={formData.Target_variable} onChange={v => setFormData({...formData, Target_variable: v})} styles={cs} menuPortalTarget={document.body} placeholder="Select target..." /></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={handleSaveParameters} disabled={loading} style={{ padding: '10px 28px', background: loading ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '6px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                  {loading ? 'Saving...' : 'Save & Continue →'}
                </button>
              </div>
            </div></div>
          )}
        </div>
      )}

      {step === 1 && (
        <div>
          <div style={gs}><div style={ghs}>📋 Parameters Summary</div><div style={gcs}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
              <p><strong>Dataset:</strong> {selectedDataset?.label}</p>
              <p><strong>Method:</strong> {savedParams.Imputation_method}</p>
              <p><strong>Risk Driver:</strong> {savedParams.risk_driver}</p>
              <p><strong>Target:</strong> {savedParams.Target_variable}</p>
            </div>
          </div></div>

          {savedParams.Imputation_method === 'Constant' && (
            <div style={gs}><div style={ghs}>🔧 Method Options</div><div style={gcs}><div style={{ maxWidth: 400 }}>
              <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Constant Value</label>
              <input type="text" value={formData.constant_value} onChange={e => setFormData({...formData, constant_value: e.target.value})} placeholder="e.g. 0" style={inputStyle} />
            </div></div></div>
          )}

          {savedParams.Imputation_method === 'Mean/Median/Mode' && (
            <div style={gs}><div style={ghs}>🔧 Method Options</div><div style={gcs}><div style={{ maxWidth: 400 }}>
              <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Statistic</label>
              <select value={formData.mean_median_mode} onChange={e => setFormData({...formData, mean_median_mode: e.target.value})} style={inputStyle}>
                <option value="Mean">Mean</option><option value="Median">Median</option><option value="Mode">Mode</option>
              </select>
            </div></div></div>
          )}

          {savedParams.Imputation_method === 'KNN' && (
            <div style={gs}><div style={ghs}>🔧 Method Options</div><div style={gcs}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 500 }}>
                <div><label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Neighbors (k)</label><input type="number" value={formData.n_neighbors} onChange={e => setFormData({...formData, n_neighbors: parseInt(e.target.value)})} min="1" max="20" style={inputStyle} /></div>
                <div><label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Weights</label><select value={formData.weights} onChange={e => setFormData({...formData, weights: e.target.value})} style={inputStyle}><option value="uniform">Uniform</option><option value="distance">Distance</option></select></div>
              </div>
            </div></div>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button onClick={() => setStep(0)} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>← Back</button>
            <button onClick={handleRunImputation} disabled={loading} style={{ padding: '10px 24px', background: loading ? '#95a5a6' : '#3498db', color: 'white', border: 'none', borderRadius: 6, cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13 }}>{loading ? 'Running...' : '🚀 Run Imputation'}</button>
          </div>
        </div>
      )}

      {step === 2 && results && (
        <div>
          <div style={{ marginBottom: '20px', background: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>{chartTitle}</h4>
              <button onClick={() => setShowChartEditor(!showChartEditor)} style={{ padding: '2px 8px', fontSize: '11px', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>✏️ Edit</button>
            </div>
            {showChartEditor && (
              <div style={{ display: 'flex', gap: '8px', padding: '8px', background: '#f9f9f9', borderRadius: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <input placeholder="Title" value={chartTitle} onChange={e => setChartTitle(e.target.value)} style={{ flex: 2, fontSize: '12px', padding: '4px' }} />
                <input placeholder="X axis" value={chartXLabel} onChange={e => setChartXLabel(e.target.value)} style={{ flex: 1, fontSize: '12px', padding: '4px' }} />
                <input placeholder="Y axis" value={chartYLabel} onChange={e => setChartYLabel(e.target.value)} style={{ flex: 1, fontSize: '12px', padding: '4px' }} />
                <button onClick={() => { localStorage.setItem('mi_chart_title', chartTitle); localStorage.setItem('mi_chart_xlabel', chartXLabel); localStorage.setItem('mi_chart_ylabel', chartYLabel); setShowChartEditor(false); }}
                  style={{ background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 12px', cursor: 'pointer', fontSize: '10px' }}>Apply</button>
              </div>
            )}
            <InteractivePlot plotData={results.plot_json} riskDriver={savedParams.risk_driver} targetVar={savedParams.Target_variable} title={chartTitle} xLabel={chartXLabel} yLabel={chartYLabel} />
          </div>

          <div style={gs}><div style={ghs}>📊 Before/After Summary</div><div style={gcs}>
            <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: '#2c3e50', position: 'sticky', top: 0, zIndex: 1 }}>{tableHeaders.map(h => (<th key={h} style={{ color: 'white', fontWeight: 600, padding: '8px 12px', textAlign: 'left', fontSize: '11px' }}>{h}</th>))}</tr></thead>
                <tbody>{results.data?.map((row, i) => (<tr key={i} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? 'white' : '#fafafa' }}><td style={{ padding: '6px 12px', fontFamily: 'monospace' }}>{row.Category}</td><td style={{ padding: '6px 12px' }}>{row.obs_before?.toLocaleString()}</td><td style={{ padding: '6px 12px' }}>{row.obs_after?.toLocaleString()}</td><td style={{ padding: '6px 12px' }}>{row.pct_before?.toFixed(2)}%</td><td style={{ padding: '6px 12px' }}>{row.pct_after?.toFixed(2)}%</td><td style={{ padding: '6px 12px' }}>{row.avg_target_before?.toFixed(6)}</td><td style={{ padding: '6px 12px' }}>{row.avg_target_after?.toFixed(6)}</td></tr>))}</tbody>
              </table>
            </div>
          </div></div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}><div><label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 4 }}>Column Name</label><input type="text" value={formData.new_column_name} onChange={e => setFormData({...formData, new_column_name: e.target.value})} placeholder="imputed_col" style={{ padding: '8px 12px', fontSize: 12, border: '1px solid #ddd', borderRadius: 6, width: 160 }} /></div><button onClick={handleSaveColumn} disabled={saving} style={{ padding: '8px 20px', background: saving ? '#95a5a6' : '#27ae60', color: 'white', border: 'none', borderRadius: 6, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>{saving ? 'Saving...' : '💾 Save'}</button></div>
            <div style={{ display: 'flex', gap: 12 }}><button onClick={() => setStep(1)} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>← Back</button><button onClick={handleExport} style={{ padding: '8px 20px', background: '#3498db', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>📥 Export</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MissingImputation;